import type { EntityData } from '@adornis/baseql/entities/types.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { iban } from '@adornis/validation/functions/iban.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { maxLength } from '@martinus/validation/maxLength.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import type { Person } from '../../../entities/person.js';
import {
  ANVermoegenswirksameLeistungen,
  VWLVertragsArt,
} from '../../entities/employee-sections/an-vermoegenswirksameleistungen.js';

export const EmployeeSectionVermoegenswirksameLeistungenForm = stateful(
  (props: { form: FunctionalForm<EntityData<Person>> }) => {
    return html`
      <div class="form-grid">
        ${FormGridEntry({
          label: 'Haben Sie vermögenswirksame Leistungen?',
          render: ToggleInput({
            ...props.form.field(
              'mitarbeiterDaten',
              'anVermoegenswirksameLeitungen',
              'vermoegenswirksameLeistungIstVorhanden',
            ),
            label: props.form.document.mitarbeiterDaten?.anVermoegenswirksameLeitungen
              ?.vermoegenswirksameLeistungIstVorhanden
              ? 'Vermögenswirksame Leistungen vorhanden'
              : 'Vermögenswirksame Leistungen nicht vorhanden',
            onPicked: value => {
              if (
                value &&
                props.form.document?.mitarbeiterDaten &&
                !props.form.document.mitarbeiterDaten.anVermoegenswirksameLeitungen
              ) {
                props.form.document.mitarbeiterDaten.anVermoegenswirksameLeitungen = new ANVermoegenswirksameLeistungen(
                  {},
                );
              }
              props.form
                .field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'vermoegenswirksameLeistungIstVorhanden')
                .onPicked?.(value);
            },
          }),
        })}
        ${
          props.form.document.mitarbeiterDaten?.anVermoegenswirksameLeitungen?.vermoegenswirksameLeistungIstVorhanden
            ? html`
                <p class="col-span-2 mt-4 mb-4 badge badge-ghost">
                  Bei Vollzeit beträgt der Arbeitgeberzuschuss 6,65 €. Bei Teilzeit erhalten Sie den anteiligen Betrag.
                </p>
                ${FormGridEntry({
                  label: 'Arbeitgeberblatt',
                  render: FileInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'arbeitgeberblattID'),
                    placeholder: 'Vertrag für die vermögenswirksame Leistungen',
                    placeholderMode: 'prefix',
                  }),
                  labelSubtext:
                    'Anstatt alles auszufüllen, können Sie uns auch eine Bescheinigung für den Arbeitgeber von Ihrem Anbieter hier hochladen.',
                })}
                ${FormGridEntry({
                  label: 'Vermögensbildung',
                  render: NumberInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'vermoegensbildung'),
                    suffix: '€',
                    placeholder: 'Keine Vermögensbildung hinterlegt',
                    placeholderMode: 'hide',
                  }),
                })}
                ${FormGridEntry({
                  label: 'Arbeitgeber-Anteil Vermögensbildung',
                  render: NumberInput({
                    ...props.form.field(
                      'mitarbeiterDaten',
                      'anVermoegenswirksameLeitungen',
                      'vermoegensbildungArbeitgeberAnteil',
                    ),
                    suffix: '€',
                    placeholder: 'Keine Arbeitgeber-Anteil Vermögensbildung hinterlegt',
                    placeholderMode: 'hide',
                  }),
                })}
                ${FormGridEntry({
                  label: 'Lastschrifteinzug',
                  render: CheckboxInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'lastschrifteinzug'),
                    label: props.form.document.mitarbeiterDaten?.anVermoegenswirksameLeitungen?.lastschrifteinzug
                      ? 'Ja, Lastschrifteinzug zugestimmt'
                      : 'Nein, Lastschrifteinzug nicht zugestimmt',
                  }),
                })}
                ${FormGridEntry({
                  label: 'IBAN',
                  render: TextInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'iban'),
                    placeholder: 'Keine IBAN hinterlegt',
                    placeholderMode: 'hide',
                    clearable: true,
                  }),
                })}
                ${FormGridEntry({
                  label: 'Institut',
                  render: TextInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'institut'),
                    placeholder: 'Kein Institut hinterlegt',
                    placeholderMode: 'hide',
                    clearable: true,
                  }),
                })}
                ${FormGridEntry({
                  label: 'Vertragsnummer',
                  render: TextInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'vertragsnummer'),
                    placeholder: 'Keine Vertragsnummer hinterlegt',
                    placeholderMode: 'hide',
                    clearable: true,
                  }),
                })}
                ${FormGridEntry({
                  label: 'Vertragsart',
                  render: DropdownSelectionInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'vertragsart'),
                    placeholder: 'Keine Vertragsart hinterlegt',
                    placeholderMode: 'hide',
                    selectables: Object.values(VWLVertragsArt),
                    clearable: true,
                  }),
                })}
                ${FormGridEntry({
                  label: 'Altersvermögenswirksame Leistung',
                  render: CheckboxInput({
                    ...props.form.field(
                      'mitarbeiterDaten',
                      'anVermoegenswirksameLeitungen',
                      'isAltersvermoegenswirksameLeistung',
                    ),
                    label: props.form.document.mitarbeiterDaten?.anVermoegenswirksameLeitungen
                      ?.isAltersvermoegenswirksameLeistung
                      ? 'Ja, Altersvermögenswirksame Leistung'
                      : 'Nein, Keine Altersvermögenswirksame Leistung',
                  }),
                })}
                ${FormGridEntry({
                  label: 'Vertrags-Beginn',
                  render: DateInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'vertragBegin'),
                    placeholder: 'Kein Vertrags-Beginn hinterlegt',
                    placeholderMode: 'hide',
                  }),
                })}
                ${FormGridEntry({
                  label: 'Vertrags-Ende',
                  render: DateInput({
                    ...props.form.field('mitarbeiterDaten', 'anVermoegenswirksameLeitungen', 'vertragEnd'),
                    placeholder: 'Kein Vertrags-Ende hinterlegt',
                    placeholderMode: 'hide',
                  }),
                })}
              `
            : nothing
        }
      </div>
    `;
  },
);

export const EmployeeSectionVermoegenswirksameLeistungenFormValidator = {
  iban: options => {
    if (!options.target.vermoegenswirksameLeistungIstVorhanden) return;
    return iban({ allowEmpty: true })(options);
  },
  institut: options => {
    if (!options.target.vermoegenswirksameLeistungIstVorhanden) return;
    return maxLength(22, { allowFalsy: true })(options);
  },
  vertragsnummer: options => {
    if (!options.target.vermoegenswirksameLeistungIstVorhanden) return;
    return maxLength(20, { allowFalsy: true })(options);
  },
  vertragsart: options => {
    if (!options.target.vermoegenswirksameLeistungIstVorhanden) return;
    return sgNonOptional()(options);
  },
  vertragEnd: options => {
    if (!options.target.vermoegenswirksameLeistungIstVorhanden) return;
    if (!options.value && options.value >= options.target.vertragBegin)
      throw new ValidationError('Vertragsende der Vermögenswirksamen Leistung muss nach Vertragsbeginn liegen', {
        target: options.target,
        key: options.key,
        path: options.path,
      });
  },
};
